import nextId from 'react-id-generator';
import cloneDeep from 'lodash.clonedeep';
import { JurisdictionActions } from '@store/actions';
import { browseReducer } from '../../core/utils/methods/browse-sorter-reducer';
import { createReducer, on } from '../../core/utils/store';
import {
  AddPermitTypeData,
  FinancialData,
  JurisdictionBrowse,
  JurisdictionDetails,
  JurisdictionDetailsContacts,
  JurisdictionDetailsPermitting,
  JurisdictionDetailsPermittingType,
  JurisdictionDetailsZoning,
  JurisdictionModalValues,
  JurisdictionOption,
  JurisdictionPermittingData,
  JurisdictionSummaryZoning,
  JurisdictionZoningData,
  PaginatedResponse,
  PayloadAndState,
  PermittingBrowse,
  PermittingContactsData,
  PermittingDetails,
  PermittingJurisdictionAuditBody,
  PermittingJurisdictionAuditData,
  PermittingSummaryData,
  PermittingWorkflowBody,
  PermittingWorkflowData,
} from '../../models/interfaces';
import {
  JurisdictionPermittingSectionType,
  RemovePermitTypeData,
} from '../../models/types';
import { PermittingActions } from '../actions/permitting.actions';

export interface PermittingState {
  browseData: PaginatedResponse<PermittingBrowse>;
  jurisdictionList: JurisdictionOption[];
  detailsData: PermittingDetails;
  jurisdictionBrowseData: PaginatedResponse<JurisdictionBrowse>;
  jurisdictionDetailsData: JurisdictionDetails;
  loadedJurisdictionTabs: Record<string, boolean>;
  isFetching: boolean;
  errors: boolean;
  originalPermitTypeTableData: {
    [permittingId: string]: JurisdictionDetailsPermittingType[];
  };
  currentPermitTypeTableData: {
    [permittingId: string]: JurisdictionDetailsPermittingType[];
  };
  loadingTabs: Record<string, boolean>;
  loadedTabs: Record<string, boolean>;
  tabData: {
    summary: PermittingSummaryData;
    workflow: PermittingWorkflowData;
    contacts: PermittingContactsData;
    jurisdictionAudit: PermittingJurisdictionAuditData;
    financial: FinancialData;
  };
}

const initialState: PermittingState = {
  browseData: { items: [], total: 0 },
  jurisdictionList: [],
  detailsData: {} as PermittingDetails,
  jurisdictionBrowseData: { items: [], total: 0 },
  jurisdictionDetailsData: {} as JurisdictionDetails,
  loadingTabs: {},
  loadedJurisdictionTabs: {},
  isFetching: false,
  errors: false,
  originalPermitTypeTableData: {},
  currentPermitTypeTableData: {},
  loadedTabs: {},
  tabData: {
    summary: {} as PermittingSummaryData,
    workflow: {
      BPExpiration: null,
      projectPermittingNote: '',
      jurisdiction: null,
    },
    contacts: {} as PermittingContactsData,
    jurisdictionAudit: {} as PermittingJurisdictionAuditData,
    financial: {} as FinancialData,
  },
};

const generateId = () => nextId('PermitType');

const getProcessedPermitTypes = (
  permitTypes?: JurisdictionDetailsPermittingType[] | undefined,
) =>
  (permitTypes || []).map((permitType: JurisdictionDetailsPermittingType) => ({
    ...permitType,
    generatedId: generateId(),
  }));

export const reducer = createReducer(
  initialState,
  // GET PERMITTING BROWSE DATA
  on(PermittingActions.getPermittingBrowseData, browseReducer('browseData')),
  // get jurisdiction list
  on(
    PermittingActions.getJurisdictionList,
    ({
      payload: jurisdictionList,
    }: PayloadAndState<JurisdictionOption[], PermittingState>) => ({
      jurisdictionList,
    }),
  ),
  // get details data
  on(
    PermittingActions.getPermittingDetailsData,
    ({
      payload: detailsData,
    }: PayloadAndState<PermittingDetails, PermittingState>) => {
      const processedPermitTypes = getProcessedPermitTypes(
        detailsData?.site?.jurisdiction?.permitting?.permittingTypes,
      );

      const permittingId =
        detailsData?.site?.jurisdiction?.permitting?.id || '';

      return {
        detailsData,
        originalPermitTypeTableData: { [permittingId]: processedPermitTypes },
        currentPermitTypeTableData: { [permittingId]: processedPermitTypes },
      };
    },
  ),

  // Set tab loading state
  on(PermittingActions.setTabLoading, ({ payload, state }) => ({
    ...state,
    loadingTabs: {
      ...state.loadingTabs,
      [payload.tabName]: payload.isLoading,
    },
  })),

  // reset tab data
  on(PermittingActions.resetTabsData, ({ state }): PermittingState => {
    return {
      ...state,
      tabData: initialState.tabData,
      loadingTabs: {},
      loadedTabs: {},
      originalPermitTypeTableData: {},
      currentPermitTypeTableData: {},
    };
  }),

  // Load summary tab data
  on(PermittingActions.getSummaryTabData, ({ payload, state }) => ({
    ...state,
    loadingTabs: { ...state.loadingTabs, summary: false },
    loadedTabs: { ...state.loadedTabs, summary: true },
    tabData: {
      ...state.tabData,
      summary: payload,
    },
  })),

  // Load workflow tab data
  on(
    PermittingActions.getWorkflowTabData,
    ({
      payload: workflowData,
      state,
    }: PayloadAndState<PermittingWorkflowData, PermittingState>) => {
      const permittingTypes =
        workflowData?.jurisdiction?.permitting?.permittingTypes;
      const processedPermitTypes = permittingTypes
        ? getProcessedPermitTypes(permittingTypes)
        : [];

      const permittingId = workflowData?.jurisdiction?.permitting?.id || '';

      return {
        tabData: {
          ...state.tabData,
          workflow: workflowData,
        },
        loadingTabs: {
          ...state.loadingTabs,
          workflow: false,
        },
        loadedTabs: {
          ...state.loadedTabs,
          workflow: true,
        },
        originalPermitTypeTableData: {
          ...state.originalPermitTypeTableData,
          [permittingId]: processedPermitTypes,
        },
        currentPermitTypeTableData: {
          ...state.currentPermitTypeTableData,
          [permittingId]: processedPermitTypes,
        },
      };
    },
  ),

  // Load contacts tab data
  on(PermittingActions.getContactsTabData, ({ payload, state }) => ({
    ...state,
    loadingTabs: { ...state.loadingTabs, contacts: false },
    loadedTabs: { ...state.loadedTabs, contacts: true },
    tabData: {
      ...state.tabData,
      contacts: { contacts: payload },
    },
  })),

  // Load jurisdiction audit tab data
  on(PermittingActions.getJurisdictionAuditTabData, ({ payload, state }) => {
    const permittingTypes = payload?.permitting?.permittingTypes;
    const processedPermitTypes = permittingTypes
      ? getProcessedPermitTypes(permittingTypes)
      : [];

    const permittingId = payload?.permitting?.id || '';

    return {
      ...state,
      loadingTabs: { ...state.loadingTabs, jurisdictionAudit: false },
      loadedTabs: { ...state.loadedTabs, jurisdictionAudit: true },
      tabData: {
        ...state.tabData,
        jurisdictionAudit: payload,
      },
      originalPermitTypeTableData: {
        ...state.originalPermitTypeTableData,
        [permittingId]: processedPermitTypes,
      },
      currentPermitTypeTableData: {
        ...state.currentPermitTypeTableData,
        [permittingId]: processedPermitTypes,
      },
    };
  }),

  // Load financial tab data
  on(PermittingActions.getFinancialTabData, ({ payload, state }) => ({
    ...state,
    loadingTabs: { ...state.loadingTabs, financial: false },
    loadedTabs: { ...state.loadedTabs, financial: true },
    tabData: {
      ...state.tabData,
      financial: payload,
    },
  })),

  // reset permitting types
  on(
    PermittingActions.resetPermittingTypesAction,
    ({
      payload: permittingIds,
      state: { currentPermitTypeTableData, originalPermitTypeTableData },
    }: PayloadAndState<string[], PermittingState>) => {
      const result = cloneDeep(currentPermitTypeTableData);

      permittingIds.forEach((id: string) => {
        result[id] = cloneDeep(originalPermitTypeTableData?.[id] || []);
      });

      return {
        currentPermitTypeTableData: result,
      };
    },
  ),
  // set permitting types
  on(
    PermittingActions.setPermittingTypesAction,
    ({
      payload: permittingIds,
      state: { currentPermitTypeTableData, originalPermitTypeTableData },
    }: PayloadAndState<string[], PermittingState>) => {
      const result = cloneDeep(originalPermitTypeTableData);

      permittingIds.forEach((id: string) => {
        result[id] = getProcessedPermitTypes(currentPermitTypeTableData[id]);
      });

      return {
        originalPermitTypeTableData: result,
        currentPermitTypeTableData: result,
      };
    },
  ),
  // add permitting type
  on(
    PermittingActions.permittingAddPermittingType,
    ({
      payload: { data, permittingId },
      state: { currentPermitTypeTableData },
    }: PayloadAndState<AddPermitTypeData, PermittingState>) => ({
      currentPermitTypeTableData: {
        ...currentPermitTypeTableData,
        [permittingId]: [
          ...(currentPermitTypeTableData[permittingId] || []),
          { ...data, generatedId: generateId() },
        ],
      },
    }),
  ),
  // update permitting type
  on(
    PermittingActions.permittingUpdatePermittingType,
    ({
      payload: { data, permittingId },
      state: { currentPermitTypeTableData },
    }: PayloadAndState<AddPermitTypeData, PermittingState>) => ({
      currentPermitTypeTableData: {
        ...currentPermitTypeTableData,
        [permittingId]: [
          ...(currentPermitTypeTableData[permittingId] || []).filter(
            (permitType: JurisdictionDetailsPermittingType) =>
              permitType.generatedId !== data.generatedId,
          ),
          data,
        ],
      },
    }),
  ),
  // remove permitting type
  on(
    PermittingActions.permittingRemovePermittingType,
    ({
      payload: { permittingTypeId, permittingId },
      state: { currentPermitTypeTableData },
    }: PayloadAndState<RemovePermitTypeData, PermittingState>) => ({
      currentPermitTypeTableData: {
        ...currentPermitTypeTableData,
        [permittingId]: (currentPermitTypeTableData[permittingId] || []).filter(
          (permitType: JurisdictionDetailsPermittingType) =>
            permitType.generatedId !== permittingTypeId,
        ),
      },
    }),
  ),
  // update jurisdiction audit data
  on(
    PermittingActions.permittingUpdateJurisdictionAuditData,
    ({
      payload: {
        summarySection: { id: jurisdictionId, ...jurisdiction },
        zoningSection,
        permittingSection,
      },
      state: { detailsData },
    }: PayloadAndState<PermittingJurisdictionAuditBody, PermittingState>) => {
      const { id: zoningId, ...zoning } = zoningSection ?? {};
      const { id: permittingId, ...permitting } = permittingSection ?? {};

      return {
        detailsData: {
          ...detailsData,
          site: {
            ...detailsData.site,
            jurisdiction: {
              ...detailsData.site.jurisdiction,
              ...jurisdiction,
              zoning: {
                ...detailsData.site.jurisdiction!.zoning,
                ...zoning,
              },
              permitting: {
                ...detailsData.site.jurisdiction!.permitting,
                ...permitting,
              },
            },
          },
        } as PermittingDetails,
      };
    },
  ),
  // update permittting workflow permittingFields
  on(
    PermittingActions.permittingUpdateWorkflowData,
    ({
      state,
      payload: { projectPermittingNote, BPExpiration },
    }: PayloadAndState<PermittingWorkflowBody, PermittingState>) => {
      return {
        tabData: {
          ...state.tabData,
          loadingTabs: {
            ...state.loadingTabs,
            workflow: false,
          },
          workflow: {
            ...state.tabData.workflow,
            loaded: true,
            projectPermittingNote:
              projectPermittingNote ??
              state.tabData.workflow.projectPermittingNote,
            BPExpiration: BPExpiration ?? state.tabData.workflow.BPExpiration,
          },
        },
      };
    },
  ),
  // JURISDICTION
  // get jurisdiction browse data
  on(
    JurisdictionActions.getJurisdictionBrowseData,
    ({
      payload: jurisdictionBrowseData,
    }: PayloadAndState<
      PaginatedResponse<JurisdictionBrowse>,
      PermittingState
    >) => ({
      jurisdictionBrowseData,
    }),
  ),
  // add jurisdiction
  on(
    JurisdictionActions.addJurisdiction,
    ({
      payload: jurisdictionBrowseData,
    }: PayloadAndState<
      PaginatedResponse<JurisdictionBrowse>,
      PermittingState
    >) => ({
      jurisdictionBrowseData,
    }),
  ),
  // ZONING PANEL

  // get jurisdiction zoning details data
  on(
    JurisdictionActions.getJurisdictionZoning,
    ({
      state,
      payload,
    }: PayloadAndState<JurisdictionSummaryZoning, PermittingState>) => {
      return {
        jurisdictionDetailsData: {
          ...state.jurisdictionDetailsData,
          ...payload,
        },
      };
    },
  ),

  // add jurisdiction zoning panel
  on(
    JurisdictionActions.addJurisdictionZoningPanel,
    ({
      payload,
      state: { jurisdictionDetailsData },
    }: PayloadAndState<JurisdictionDetailsZoning, PermittingState>) => ({
      jurisdictionDetailsData: {
        ...jurisdictionDetailsData,
        zoning: [...jurisdictionDetailsData.zoning, payload],
      },
    }),
  ),
  // update jurisdiction zoning panel
  on(
    JurisdictionActions.updateJurisdictionZoningPanel,
    ({
      payload,
      state: { jurisdictionDetailsData },
    }: PayloadAndState<JurisdictionModalValues, PermittingState>) => ({
      jurisdictionDetailsData: {
        ...jurisdictionDetailsData,
        zoning: jurisdictionDetailsData.zoning.map(
          (zoning: JurisdictionDetailsZoning) =>
            zoning.id !== payload.id
              ? zoning
              : {
                  ...zoning,
                  ...payload,
                },
        ),
      },
    }),
  ),
  // remove jurisdiction zoning panel
  on(
    JurisdictionActions.removeJurisdictionZoningPanel,
    ({
      payload: id,
      state: { jurisdictionDetailsData },
    }: PayloadAndState<string, PermittingState>) => ({
      jurisdictionDetailsData: {
        ...jurisdictionDetailsData,
        zoning: [
          ...jurisdictionDetailsData.zoning.filter(
            (zoning: JurisdictionDetailsZoning) => zoning.id !== id,
          ),
        ],
      },
    }),
  ),
  // JURISDICTION ZONING
  // update jurisdiction zoning
  on(
    JurisdictionActions.updateJurisdictionZoning,
    ({
      payload: { summarySection, zoningSection },
      state: { jurisdictionDetailsData },
    }: PayloadAndState<JurisdictionZoningData, PermittingState>) => ({
      jurisdictionDetailsData: {
        ...jurisdictionDetailsData,
        ...summarySection,
        zoning: jurisdictionDetailsData.zoning.map(
          (zoning: JurisdictionDetailsZoning) => {
            const currentUpdatedZoning = zoningSection.find(
              (updatedZoning: JurisdictionDetailsZoning) =>
                updatedZoning.id === zoning.id,
            );

            if (currentUpdatedZoning) {
              const { id, projectType, projectSubType, ...updatedZoningData } =
                currentUpdatedZoning;

              return {
                ...zoning,
                ...updatedZoningData,
              };
            }

            return zoning;
          },
        ),
      },
    }),
  ),

  // PERMITTING PANEL

  // add jurisdiction permitting panel getJurisdictionPermitting
  on(
    JurisdictionActions.getJurisdictionPermitting,
    ({
      state,
      payload,
    }: PayloadAndState<JurisdictionDetailsPermitting[], PermittingState>) => {
      const permitTypeTablesData = payload.reduce(
        (acc, { id, permittingTypes }) => ({
          ...acc,
          [id]: getProcessedPermitTypes(permittingTypes),
        }),
        {},
      );

      return {
        loadingTabs: { ...state.loadingTabs, permitting: false },
        loadedJurisdictionTabs: {
          ...state.loadedJurisdictionTabs,
          permitting: true,
        },
        jurisdictionDetailsData: {
          ...state.jurisdictionDetailsData,
          permitting: payload,
        },
        originalPermitTypeTableData: permitTypeTablesData,
        currentPermitTypeTableData: permitTypeTablesData,
      };
    },
  ),

  // add jurisdiction permitting panel
  on(
    JurisdictionActions.addJurisdictionPermittingPanel,
    ({
      payload,
      state: {
        jurisdictionDetailsData,
        originalPermitTypeTableData,
        currentPermitTypeTableData,
      },
    }: PayloadAndState<JurisdictionDetailsPermitting, PermittingState>) => ({
      jurisdictionDetailsData: {
        ...jurisdictionDetailsData,
        permitting: [...jurisdictionDetailsData.permitting, payload],
      },
      originalPermitTypeTableData: {
        ...originalPermitTypeTableData,
        [payload.id]: [],
      },
      currentPermitTypeTableData: {
        ...currentPermitTypeTableData,
        [payload.id]: [],
      },
    }),
  ),
  // update jurisdiction permitting panel
  on(
    JurisdictionActions.updateJurisdictionPermittingPanel,
    ({
      payload,
      state: { jurisdictionDetailsData },
    }: PayloadAndState<JurisdictionModalValues, PermittingState>) => ({
      jurisdictionDetailsData: {
        ...jurisdictionDetailsData,
        permitting: jurisdictionDetailsData.permitting.map(
          (permitting: JurisdictionDetailsPermitting) =>
            permitting.id !== payload.id
              ? permitting
              : {
                  ...permitting,
                  ...payload,
                },
        ),
      },
    }),
  ),
  // remove jurisdiction permitting panel
  on(
    JurisdictionActions.removeJurisdictionPermittingPanel,
    ({
      payload: id,
      state: { jurisdictionDetailsData },
    }: PayloadAndState<string, PermittingState>) => ({
      jurisdictionDetailsData: {
        ...jurisdictionDetailsData,
        permitting: [
          ...jurisdictionDetailsData.permitting.filter(
            (permitting: JurisdictionDetailsPermitting) => permitting.id !== id,
          ),
        ],
      },
    }),
  ),
  // JURISDICTION PERMITTING
  // update jurisdiction PERMITTING
  on(
    JurisdictionActions.updateJurisdictionPermitting,
    ({
      payload: { summarySection, permittingSection },
      state: { jurisdictionDetailsData },
    }: PayloadAndState<JurisdictionPermittingData, PermittingState>) => ({
      jurisdictionDetailsData: {
        ...jurisdictionDetailsData,
        ...summarySection,
        permitting: jurisdictionDetailsData.permitting.map(
          (permitting: JurisdictionDetailsPermitting) => {
            const currentUpdatedPermitting = permittingSection.find(
              (updatedPermitting: JurisdictionPermittingSectionType) =>
                updatedPermitting.id === permitting.id,
            );

            if (currentUpdatedPermitting) {
              const {
                id,
                projectType,
                projectSubType,
                ...updatedPermittingData
              } = currentUpdatedPermitting;

              return {
                ...permitting,
                ...updatedPermittingData,
              };
            }

            return permitting;
          },
        ),
      },
    }),
  ),
  // JURISDICTION - CONTACTS
  // add jurisdiction contact
  on(
    JurisdictionActions.addJurisdictionContact,
    ({
      payload,
      state: { jurisdictionDetailsData },
    }: PayloadAndState<JurisdictionDetailsContacts, PermittingState>) => ({
      jurisdictionDetailsData: {
        ...jurisdictionDetailsData,
        contacts: [...jurisdictionDetailsData.contacts, payload],
      },
    }),
  ),

  // Load contacts tab data
  on(JurisdictionActions.getJurisdictionContacts, ({ payload, state }) => ({
    ...state,
    loadingTabs: { ...state.loadingTabs, contacts: false },
    loadedJurisdictionTabs: { ...state.loadedJurisdictionTabs, contacts: true },
    jurisdictionDetailsData: {
      ...state.jurisdictionDetailsData,
      contacts: payload,
    },
  })),

  // reset tab data
  on(JurisdictionActions.resetTabsData, ({ state }): PermittingState => {
    return {
      ...state,
      jurisdictionDetailsData: initialState.jurisdictionDetailsData,
      loadedJurisdictionTabs: {},
      loadingTabs: {},
      originalPermitTypeTableData: {},
      currentPermitTypeTableData: {},
    };
  }),

  // update jurisdiction contact
  on(
    JurisdictionActions.updateJurisdictionContact,
    ({
      payload,
      state: { jurisdictionDetailsData },
    }: PayloadAndState<JurisdictionDetailsContacts, PermittingState>) => ({
      jurisdictionDetailsData: {
        ...jurisdictionDetailsData,
        contacts: jurisdictionDetailsData.contacts.map(
          (contact: JurisdictionDetailsContacts) =>
            contact.id !== payload.id ? contact : { ...contact, ...payload },
        ),
      },
    }),
  ),
  // remove jurisdiction contact
  on(
    JurisdictionActions.removeJurisdictionContact,
    ({
      payload: id,
      state: { jurisdictionDetailsData },
    }: PayloadAndState<string, PermittingState>) => ({
      jurisdictionDetailsData: {
        ...jurisdictionDetailsData,
        contacts: [
          ...jurisdictionDetailsData.contacts.filter(
            (contact: JurisdictionDetailsContacts) => contact.id !== id,
          ),
        ],
      },
    }),
  ),
);
